import React from 'react';
import { Box, Card, Typography, List, ListItem, ListItemText, Avatar, Button, CircularProgress } from '@mui/material';
import { EmptyState } from '@components/DataTable/components/EmptyState';

interface JobOffer {
    _id: string;
    title: string;
    city: string;
    jobNumber: string;
    createdDate: string;
    recruitment?: {
        method: 'CLASSIC' | 'PREMIUM';
    };
    brand?: {
        brandLogo?: {
            url: string;
        };
    };
    pro?: {
        brandLogo?: {
            url: string;
        };
    };
}

interface NewJobOffersProps {
    offers: JobOffer[];
    title: string;
    count?: number;
    loading?: boolean;
    onViewAll?: () => void;
}

export const NewJobOffers: React.FC<NewJobOffersProps> = ({
    offers = [],
    title,
    count = 0,
    loading,
    onViewAll,
}) => {
    const formatDate = (date: string) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Aujourd\'hui';
        if (days === 1) return 'Hier';
        return `il y a ${days} jours`;
    };

    return (
        <Card
            sx={{
                borderRadius: '16px',
                p: 2.5,
                height: '100%',
                bgcolor: theme => (theme.palette.mode === 'dark' ? '#1a1a2e' : '#fff'),
                border: '1px solid',
                borderColor: theme => (theme.palette.mode === 'dark' ? '#2d2d44' : '#e0e0e0'),
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{title}</Typography>
                </Box>
                <Button
                    onClick={onViewAll}
                    variant="text"
                    color="secondary"
                    sx={{ py: 0, px: 0.5, borderRadius: '4px', lineHeight: 1 }}
                    endIcon={<i className="fi fi-br-angle-small-right" style={{ fontSize: '16px' }} />}>
                    Voir tout
                </Button>
            </Box>

            <List sx={{ p: 0, maxHeight: '400px', overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : offers.length > 0 ? (
                    offers.map((offer) => (
                        <ListItem
                            key={offer._id}
                            sx={{
                                px: 0,
                                py: 1.5,
                                borderBottom: '1px solid',
                                borderColor: theme => (theme.palette.mode === 'dark' ? '#2d2d44' : '#f0f0f0'),
                                '&:last-child': { borderBottom: 'none' },
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: theme => (theme.palette.mode === 'dark' ? '#252540' : '#f9f9f9'),
                                },
                            }}>
                            <Avatar
                                src={offer.brand?.brandLogo?.url || offer.pro?.brandLogo?.url || '/default-logo.png'}
                                alt={offer.title}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    mr: 1.5,
                                    mt: 0.5,
                                }}
                            />
                            <ListItemText
                                primary={
                                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                                        {offer.title}
                                    </Typography>
                                }
                                secondary={
                                    <Box>
                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                            {offer.city} • Offre #{offer.jobNumber}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
                                            {formatDate(offer.createdDate)}
                                        </Typography>
                                    </Box>
                                }
                            />
                            <i className="fi fi-br-angle-small-right" style={{ opacity: 0.5, fontSize: '14px' }} />
                        </ListItem>
                    ))
                ) : (
                    <EmptyState subtitle="Aucune nouvelle offre d'emploi" />
                )}
            </List>
        </Card>
    );
};
